import React, { useEffect, useState } from 'react';
import { Box, Paper, Typography, Button, Chip, CircularProgress, Alert } from '@mui/material';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import ApiService from '../services/api.service';

const NgeniusReturn = () => {
  const [searchParams] = useSearchParams();
  const paymentId = searchParams.get('paymentId');
  const [loading, setLoading] = useState(true);
  const [payment, setPayment] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const reconcile = async () => {
      if (!paymentId) {
        setLoading(false);
        setError('Missing paymentId');
        return;
      }
      try {
        const resp = await ApiService.post(`/api/payments/${paymentId}/refresh-approval-status`);
        setPayment(resp?.data?.data || null);
      } catch (e) {
        console.error('Failed to refresh payment status', e);
        try {
          const fallback = await ApiService.get(`/api/payments/${paymentId}`);
          setPayment(fallback?.data?.data || null);
        } catch (err) {
          setError(err?.response?.data?.error || err?.message || 'Failed to read payment status');
        }
      } finally {
        setLoading(false);
      }
    };
    reconcile();
  }, [paymentId]);

  const status = payment?.payment_status || payment?.paymentStatus;
  const invoiceId = payment?.invoice_id || payment?.invoiceId;

  let header = 'Payment is processing';
  let severity = 'info';
  if (status === 'PAID') { header = 'Payment successful'; severity = 'success'; }
  else if (status === 'REJECTED') { header = 'Payment failed'; severity = 'error'; }

  return (
    <Box sx={{ p: 3, maxWidth: 640, mx: 'auto' }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>NGenius Payment</Typography>

        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, my: 4 }}>
            <CircularProgress size={24} />
            <Typography variant="body2">Processing your payment…</Typography>
          </Box>
        )}

        {!loading && error && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}

        {!loading && !error && (
          <>
            <Alert severity={severity} sx={{ mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{header}</Typography>
              {status === 'PAID' && (
                <Typography variant="body2">Thaklis has credited the topup and tickets have been paid.</Typography>
              )}
              {status === 'REJECTED' && (
                <Typography variant="body2">
                  {payment?.error_message || payment?.errorMessage || 'Payment was declined or cancelled. You can retry from the Payments page.'}
                </Typography>
              )}
              {status !== 'PAID' && status !== 'REJECTED' && (
                <Typography variant="body2">Your payment has been submitted. The status will be reflected within 30 minutes.</Typography>
              )}
            </Alert>

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button component={RouterLink} to="/payments" variant="contained" sx={{ textTransform: 'none' }}>
                Back to Payments
              </Button>
              {invoiceId && (
                <Button component={RouterLink} to={`/invoices/${invoiceId}`} variant="outlined" sx={{ textTransform: 'none' }}>
                  View Invoice
                </Button>
              )}
              {status && (
                <Chip label={status} color={status === 'PAID' ? 'success' : status === 'REJECTED' ? 'error' : 'default'} />
              )}
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default NgeniusReturn;
